import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { effect, inject, Injectable, OutputRefSubscription } from '@angular/core';
import { EduFileTransferManagerComponent } from '@compound/file-transfer-manager/file-transfer-manager.component';
import { FileStore } from '@store/files/file.store';
import { TransferState } from '@store/files/types';

const AUTO_CLOSE_DELAY = 10_000;

@Injectable({ providedIn: 'root' })
export class FileTransferOverlayService {
  private overlay = inject(Overlay);
  private fileStore = inject(FileStore);

  private overlayRef: OverlayRef | null = null;
  private componentInstance: EduFileTransferManagerComponent | null = null;
  private closeSubscription: OutputRefSubscription | null = null;
  private autoCloseTimeoutId: ReturnType<typeof setTimeout> | null = null;

  // Indicates whether the user dismissed the overlay. Tracked alongside
  // lastActiveTransfersCount so the overlay re-opens if new transfers arrive.
  private isDismissedByUser = false;
  private lastActiveTransfersCount = 0;

  constructor() {
    effect(() => {
      const transfers = this.fileStore.transfersEntities();

      if (transfers.length === 0) {
        this.resetState();
        return;
      }

      const activeCount = transfers.filter(
        d => d.state !== TransferState.DONE && d.state !== TransferState.FAILED,
      ).length;

      const isAllSettled = activeCount === 0;
      const hasNewActiveTransfers = activeCount > this.lastActiveTransfersCount;

      // Re-open when active transfers appear, or when the user hasn't dismissed yet.
      if (activeCount > 0 && (!this.isDismissedByUser || hasNewActiveTransfers)) {
        this.open();
        this.isDismissedByUser = false;
      }

      // Schedule auto-close once all transfers settle, unless already scheduled.
      if (isAllSettled && !this.autoCloseTimeoutId) {
        this.autoCloseTimeoutId = setTimeout(() => this.close(), AUTO_CLOSE_DELAY);
      }

      this.lastActiveTransfersCount = activeCount;
    });
  }

  open(): void {
    if (this.overlayRef) return;

    const positionStrategy = this.overlay.position().global().bottom('1rem').right('1rem');

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.noop(),
      hasBackdrop: false,
    });

    const ref = this.overlayRef.attach(new ComponentPortal(EduFileTransferManagerComponent));
    this.componentInstance = ref.instance;

    this.closeSubscription = this.componentInstance.closed.subscribe(({ isDismissedByUser }) => {
      this.isDismissedByUser = isDismissedByUser;
      this.dispose();
    });
  }

  /** Triggers the component's leave animation; disposal follows via the `closed` output. */
  close(): void {
    this.componentInstance?.close();
  }

  /** Tears down the overlay, component instance, and any pending timers. */
  dispose(): void {
    if (this.autoCloseTimeoutId) {
      clearTimeout(this.autoCloseTimeoutId);
      this.autoCloseTimeoutId = null;
    }

    this.closeSubscription?.unsubscribe();
    this.overlayRef?.dispose();
    this.overlayRef = null;
    this.componentInstance = null;
  }

  private resetState(): void {
    this.isDismissedByUser = false;
    this.lastActiveTransfersCount = 0;

    if (this.autoCloseTimeoutId) {
      clearTimeout(this.autoCloseTimeoutId);
      this.autoCloseTimeoutId = null;
    }
  }
}
